import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

const mapCompTypeToEnum = (compTypeString) => {
  switch (compTypeString) {
    case 'rent':
      return 0;
    case 'sale':
      return 1;
    case 'sold':
      return 2;
  }
}

export const initialState = {
  //compsCache: {}, // caching the comps fetched from the service, structure: { task.id: { selectedCompDataType (rent, sale, sold): } }

  // SHARED WITH AUCTION UW?
  images: {}, // dictionary of all fetched images from all selected markers
  imageRequests: [],

  propertyUWClientTxnOpp: null,
  selectedOpportunityCensusTracts: [],
  selectedOpportunityMsrHomes: [],
  selectedOpportunityImportProperty: null,
  selectedOpportunityReloadStatusState: {
    isLoading: false,
    data: null,
    message: null
  },
  txnOppProformaIsOpen: true,
  selectedCT: 'amherstEstimatedAMI',
  ctMouseoverData: null,
  showCTMouseoverData: true,
  ctLegendData: null,
  selectedCompDataType: 'rent',
  selectedProformaInputsContainer: null,
  selectedValuation: null,
  transientValuation: null,
  lastCompletedValuation: null,
  valuationIsDirty: false,
  shouldCalculateProforma: false,
  saveUWDialogParameters: { dialogIsOpen: false, route: null },
  selectedOpportunityComps: [],
  selectedOpportunityImages: [],
  proformaWidth: 325,
  isSavingUW: false,
  propertyUWMapState: {
    zoom: 4,
    center: { lat: 30.2672, lng: -97.7431 },
    gestureHandling: 'auto', // Gesture Handling instead of scrollwheel for CTRL + Scroll [auto/cooperative]
    mapViewType: 'hybrid', // [hybrid/roadmap]
    autoNav: false,
    selectedMarkerLabel: 'rentprice',
    isDrawing: false,
    polygon: null,
    initialized: false
  },
  propertyUWMarkerState: {
    selectedMarker: null,
    isInfoBoxOpen: false,
    isFetchingImages: false,
    selectedMarkerImages: [],
    uncapturedMarkers: []
  },
  propertyUWMsrHomesMapState: {
    zoom: 4,
    center: { lat: 30.2672, lng: -97.7431 },
    gestureHandling: 'auto',
    mapViewType: 'hybrid',
    autoNav: false,
    initialized: false
  },
  propertyUWMsrHomesMarkerState: {
    selectedMarker: null
  },
  propertyGridCurrentViewData: [],   // Used to filter the map based on grid filters
  propertyGridRowIdToFocus: null,
  propertyUWMsrHomesGridCurrentViewData: [],
  isFetchingContainers: false,
  isFetchingMsrHomes: false,
  isFetchingTransactionEvents: false,
  isFetchingPropertyHistoryOpportunities: false,
  isFetchingValuationHistory: false,
  isFetchingPropertyUWData: false,
  isFetchingLastCompletedValuation: false,
  isFetchingPropertyUWImages: false,
  isSyncingPurchaseState: false,
  propertyUWLoadingOverlayState: {
    isLoading: false,
    message: null
  },
  compsLoadingState: {
    isLoading: false,
    message: null
  },
  isRefreshingModelTax: false,
  selectedContainerId: null,
  selectedContainer: null,
  valuationTypeModalOptions: null,
  viewContainersDialogIsOpen: false,
  containers: null,
  opportunitySfhaDetails: null, 
  isOpportunitySfhaDetailLoading: false,
  selectedOpportunityHistory: null,
  selectedOpportunityEventHistory: null,
  selectedOpportunityValuationHistory: null,
  selectedOpportunityHistoryTab: 'opportunities'
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'propertyUWSlice',
  initialState,
  reducers: {
    setPropertyUWClientTxnOpp: (state, action) => {
      state.propertyUWClientTxnOpp = action.payload;
    },
    setImagesCache: (state, action) => {
      state.images = action.payload;
    },
    setImagesRequests: (state, action) => {
      state.imageRequests = action.payload;
    },
    setSelectedOpportunityCensusTracts: (state, action) => {
      state.selectedOpportunityCensusTracts = action.payload;
    },
    setSelectedOpportunityMsrHomes: (state, action) => {
      state.selectedOpportunityMsrHomes = action.payload;
    },
    setSelectedOpportunityReloadStatusState: (state, action) => {
      state.selectedOpportunityReloadStatusState = { ...state.selectedOpportunityReloadStatusState, ...action.payload };
    },
    setSelectedOpportunityAmherstIndicative: (state, action) => {
      state.selectedOpportunityAmherstIndicative = action.payload;
    },
    toggleTxnOppProformaIsOpen: (state, action) => {
      state.txnOppProformaIsOpen = !state.txnOppProformaIsOpen;
    },
    setSelectedCT: (state, action) => {
      state.selectedCT = action.payload;
    },
    setCTMouseoverData: (state, action) => {
      state.ctMouseoverData = action.payload;
    },
    setShowCTMouseoverData: (state, action) => { // State whether or not the show census tract summary information on hover
      state.showCTMouseoverData = action.payload;
    },
    setCTLegendData: (state, action) => {
      state.ctLegendData = action.payload;
    },
    setSelectedCompDataType: (state, action) => {
      state.selectedCompDataType = action.payload;
    },
    setSelectedProformaInputsContainer: (state, action) => {
      state.selectedProformaInputsContainer = action.payload;
    },
    setSelectedValuation: (state, action) => {
      state.selectedValuation = action.payload;
    },
    setTransientValuation: { // Triggers setTransientValuationSaga
      reducer: (state, action) => {
        state.transientValuation = action.payload.transientValuation;
      },
      prepare: (transientValuation, resetInputDirtyFlags) => ({ payload: { transientValuation, resetInputDirtyFlags } })
    },
    setLastCompletedValuation: (state, action) => {
      state.lastCompletedValuation = action.payload;
    },
    setValuationIsDirty: (state, action) => { // NOTE: valuationIsDirty and shouldCalculateProforma flags work together to enbale/disable 'Save' and  'Calculate' buttons in PropertyUWProforma
      state.valuationIsDirty = action.payload;
    },
    setShouldCalculateProforma: (state, action) => {
      state.shouldCalculateProforma = action.payload;
    },
    setSaveUWDialogParameters: (state, action) => {
      state.saveUWDialogParameters = action.payload;
    },
    setSelectedOpportunityComps: (state, action) => {
      if (state.selectedOpportunityComps.length !== 0 || action.payload.length !== 0) {
        state.selectedOpportunityComps = [...action.payload];
      }
    },
    setSelectedOpportunityImages: (state, action) => {
      if (state.selectedOpportunityImages.length !== 0 || action.payload.length !== 0) {
        state.selectedOpportunityImages = [...action.payload];
      }
    },
    setProformaWidth: (state, action) => {
      state.proformaWidth = action.payload;
    },
    setIsSavingUW: (state, action) => {
      state.isSavingUW = action.payload;
    },

    setPropertyUWMapState: (state, action) => {
      state.propertyUWMapState = { ...state.propertyUWMapState, ...action.payload };
    },
    setPropertyUWMarkerState: (state, action) => { // Triggers getImagesSaga
      state.propertyUWMarkerState = { ...state.propertyUWMarkerState, ...action.payload };
    },
    setPropertyUWMsrHomesMapState: (state, action) => {
      state.propertyUWMsrHomesMapState = { ...state.propertyUWMsrHomesMapState, ...action.payload };
    },
    setPropertyUWMsrHomesMarkerState: (state, action) => { // Triggers getImagesSaga
      state.propertyUWMsrHomesMarkerState = { ...state.propertyUWMsrHomesMarkerState, ...action.payload };
    },
    setPropertyGridCurrentViewData: (state, action) => { // Used to filter the map when grid filter is applied
      state.propertyGridCurrentViewData = action.payload;
    },
    setPropertyGridRowIdToFocus: (state, action) => {
      state.propertyGridRowIdToFocus = action.payload;
    },
    setPropertyUWMsrHomesGridCurrentViewData: (state, action) => {
      state.propertyUWMsrHomesGridCurrentViewData = action.payload;
    },
    setIsFetchingContainers: (state, action) => {
      state.isFetchingContainers = action.payload;
    },
    setIsFetchingMsrHomes: (state, action) => {
      state.isFetchingMsrHomes = action.payload;
    },
    setIsFetchingTransactionEvents: (state, action) => {
      state.isFetchingTransactionEvents = action.payload;
    },
    setIsFetchingPropertyHistoryOpportunities: (state, action) => {
      state.isFetchingPropertyHistoryOpportunities = action.payload;
    },
    setIsFetchingValuationHistory: (state, action) => {
      state.isFetchingValuationHistory = action.payload;
    },
    setIsFetchingPropertyUWData: (state, action) => {
      state.isFetchingPropertyUWData = action.payload;
    },
    setIsFetchingLastCompletedValuation: (state, action) => {
      state.isFetchingLastCompletedValuation = action.payload;
    },
    setIsFetchingPropertyUWImages: (state, action) => {
      state.isFetchingPropertyUWImages = action.payload;
    },
    setIsSyncingPurchaseState: (state, action) => {
      state.isSyncingPurchaseState = action.payload;
    },
    setPropertyUWLoadingOverlayState: (state, action) => {
      state.propertyUWLoadingOverlayState = action.payload;
    },
    setCompsLoadingState: (state, action) => {
      state.compsLoadingState = { ...state.compsLoadingState, ...action.payload };
    },
    setIsRefreshingModelTax: (state, action) => {
      state.isRefreshingModelTax = action.payload;
    },
    setSelectedContainerId: {
      reducer: (state, action) => {
        state.selectedContainerId = action.payload.selectedContainerId;
      },
      prepare: (selectedContainerId, selectedValuationId, transientValuation, transactionOpportunityId, shouldEndLoading, callback) => ({ payload: { selectedContainerId, selectedValuationId, transientValuation, transactionOpportunityId, shouldEndLoading, callback } })
    },
    setSelectedContainer: (state, action) => {
      state.selectedContainer = action.payload;
    },
    setValuationTypeModalOptions: (state, action) => {
      state.valuationTypeModalOptions = action.payload;
    },
    setViewContainersDialogIsOpen: (state, action) => {
      state.viewContainersDialogIsOpen = action.payload;
    },
    setContainers: (state, action) => {
      state.containers = action.payload;
    },
    setOpportunitySfhaDetails: (state, action) => {
      state.opportunitySfhaDetails = action.payload;
    },
    setIsOpportunitySfhaDetailLoading: (state, action) => {
      state.isOpportunitySfhaDetailLoading = action.payload;
    },
    setSelectedOpportunityHistory: (state, action) => {
      state.selectedOpportunityHistory = action.payload;
    },
    setSelectedOpportunityEventHistory: (state, action) => {
      state.selectedOpportunityEventHistory = action.payload;
    },    
    setSelectedOpportunityValuationHistory: (state, action) => {
      state.selectedOpportunityValuationHistory = action.payload;
    },
    setSelectedOpportunityHistoryTab : (state, action) => {
      state.selectedOpportunityHistoryTab = action.payload;
    },

    // Redux saga actions, no reducers
    fetchUWData: { // Triggers getTaskUWDataSaga
      reducer: () => { },
      prepare: (transactionOpportunityId, selectedContainerId, history) => ({ payload: { transactionOpportunityId, selectedContainerId, history } })
    },
    fetchUWSnapshotData: { // Triggers fetchUWSnapshotData
      reducer: () => { },
      prepare: (transactionOpportunityId, selectedContainerId, history) => ({ payload: { transactionOpportunityId, selectedContainerId, history } })
    },
    fetchLastCompletedValuation: () => { }, // Triggers getTaskUWDataSaga
    fetchPropertyUWCensusTract: () => { }, // Triggers getTaskUWDataSaga
    fetchPropertyUWContainers: () => { }, // Triggers getTaskUWDataSaga
    fetchPropertyUWImages: () => { }, // Triggers getTaskUWDataSaga
    fetchPropertyUWReloadStatus: () => { }, // Triggers getTaskUWDataSaga
    fetchMsrHomes: () => { }, // Triggers getMsrHomesSaga
    fetchTransactionEvents: { // Triggers getTransactionEventsSaga
      reducer: () => { },
      prepare: (asgPropId, livingAreaSqFt, callback) => ({ payload: { asgPropId, livingAreaSqFt, callback } })
    },
    fetchOpportunitiesForPropertyHistory: { // Triggers getOpportunitiesForPropertyHistorySaga
      reducer: () => { },
      prepare: (asgPropId, callback) => ({ payload: { asgPropId, callback } })
    },
    fetchValuationHistory: { // Triggers getValuationHistorySaga
      reducer: () => { },
      prepare: (transactionOpportunityId, callback) => ({ payload: { transactionOpportunityId, callback } })
    },
    fetchOpportunitySfhaDetails: () => {},
    saveUW: () => { }, // Triggers saveUWSaga
    savePostAcqUW: () => { }, // Triggers saveUWSaga
    afterSaveUWNavigate: () => { }, // Triggers afterSaveUWNavigateSaga
    refreshModelTax: {
      reducer: () => {},
      prepare: (txnOppId, bidPrice, callback) => ({payload: {txnOppId, bidPrice, callback}})
    }, // Triggers refreshModelTaxSaga
    takeValuation: { // Triggers takeValuationSaga
      reducer: () => { },
      prepare: (valuationToTake, unmappedValuationsComps) => ({ payload: { valuationToTake, unmappedValuationsComps } })
    },
    setTransientValuationInputs: { // Triggers setTransientValuationSaga
      reducer: () => { },
      prepare: (valuationInput, skipCalculate) => ({ payload: { valuationInput, skipCalculate } })
    },
    setTransientValuationNoBidReason: () => { }, // Triggers setTransientValuationSaga
    setTransientValuationType: () => { }, // Triggers setTransientValuationTypeSaga
    setTransientValuationNotes: () => { }, // Triggers setTransientValuationSaga
    setTransientValuationAfterTaxRefresh: () => { }, // Triggers setTransientValuationSaga
    calcPropertyUWProforma: () => { }, // Triggers calcProformaSaga
    toggleIsSelectedAsComp: () => { } // Triggers toggleIsSelectedAsCompSaga
  }
});

export default slice.reducer;

export const propertyUWActions = slice.actions;

// Memoized selectors
export const propertyUWSelectors = {
  propertyUWStateSelector: createSelector(
    state => state.propertyUWState,
    propertyUWState => propertyUWState
  ),
  imagesSelector: createSelector(
    state => state.propertyUWState.images,
    images => images
  ),
  imageRequestsSelector: createSelector(
    state => state.propertyUWState.imageRequests,
    imageRequests => imageRequests
  ),
  transientValuationSelector: createSelector(
    state => state.propertyUWState.transientValuation,
    transientValuation => transientValuation
  ),
  selectedValuationSelector: createSelector(
    state => state.propertyUWState.selectedValuation,
    selectedValuation => selectedValuation
  ),
  shouldCalculateProformaSelector: createSelector(
    state => state.propertyUWState.shouldCalculateProforma,
    shouldCalculateProforma => shouldCalculateProforma
  ),
  selectedOpportunityCompsSelector: createSelector( // Full collection of comps for the selectedOpportunity
    state => state.propertyUWState.selectedOpportunityComps,
    selectedOpportunityComps => selectedOpportunityComps
  ),
  filteredSelectedOpportunityCompsSelector: createSelector( // Collection of comps for the selectedOpportunity filtered by type (Rent/For Sale/Sold)
    [state => state.propertyUWState.selectedOpportunityComps, state => state.propertyUWState.selectedCompDataType],
    (selectedOpportunityComps, selectedCompDataType) => {
      return selectedOpportunityComps.filter(comp => comp.compType === mapCompTypeToEnum(selectedCompDataType))
    }
  ),
  selectedProformaInputsContainerSelector: createSelector(
    state => state.propertyUWState.selectedProformaInputsContainer,
    selectedProformaInputsContainer => selectedProformaInputsContainer
  ),
  isFetchingPropertyUWDataSelector: createSelector(
    state => state.propertyUWState.isFetchingPropertyUWData,
    isFetchingPropertyUWData => isFetchingPropertyUWData
  ),
  isSyncingPurchaseStateSelector: createSelector(
    state => state.propertyUWState.isSyncingPurchaseState,
    isSyncingPurchaseState => isSyncingPurchaseState
  ),
  selectedContainerIdSelector: createSelector(
    state => state.propertyUWState.selectedContainerId,
    selectedContainerId => selectedContainerId
  )
}